import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Edit2, Clock, Mail, ExternalLink, RefreshCw, Copy, Check } from 'lucide-react';
import PublishInstructions from './PublishInstructions';
import { backendUrl } from '../../utils/socketClient';

interface DraftData {
  draft_content: string;
  shop_name: string;
  user_email: string;
  expires_at: number;
}

interface DraftPattern {
  id: number;
  title: string;
  content: string;
}

const DraftEditor: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [draftData, setDraftData] = useState<DraftData | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInstructions, setShowInstructions] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [showPatterns, setShowPatterns] = useState(true);
  const [patterns, setPatterns] = useState<DraftPattern[]>([]);
  const [generatingPatterns, setGeneratingPatterns] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!sessionId) return;

    fetchDraft();
    document.title = `下書き編集 | RepliGo（レプリゴ）`;
  }, [sessionId]);

  useEffect(() => {
    // 下書きデータが読み込まれたら、自動的に3つのパターンを生成
    if (draftData && !generatingPatterns && patterns.length === 0) {
      generatePatterns();
    }
  }, [draftData]);

  const fetchDraft = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/draft/${sessionId}`);
      const result = await response.json();

      if (result.success) {
        setDraftData(result);
        setEditedContent(result.draft_content);
      } else {
        setError(result.error || 'データの取得に失敗しました');
      }
    } catch (err) {
      setError('通信エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };



  const regenerateDraft = async () => {
    if (!sessionId) return;

    setRegenerating(true);
    try {
      const response = await fetch(`${backendUrl}/api/draft/${sessionId}/regenerate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ generate_multiple: false })
      });

      const result = await response.json();
      if (result.success) {
        setEditedContent(result.draft_content);
        if (draftData) {
          setDraftData({
            ...draftData,
            draft_content: result.draft_content
          });
        }
        // デバッグ情報をコンソールに表示
        if (result.debug_info) {
          console.log('=== 下書き再生成デバッグ情報 ===');
          console.log('Gemini プロンプト:', result.debug_info.prompt);
          console.log('アンケートデータ:', result.debug_info.survey_data);
          console.log('計算された評価:', result.debug_info.ratings);
          console.log('再生成された下書き:', result.debug_info.generated_content);
          console.log('==============================');
        }
        alert('下書きを再生成しました');
      } else {
        alert('再生成に失敗しました: ' + result.error);
      }
    } catch (err) {
      alert('再生成中にエラーが発生しました');
    } finally {
      setRegenerating(false);
    }
  };

  const generatePatterns = async () => {
    if (!sessionId) return;

    setGeneratingPatterns(true);
    try {
      // アンケートデータから3つのパターンを生成
      const response = await fetch(`${backendUrl}/api/draft/${sessionId}/patterns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const result = await response.json();
      if (result.success) {
        setPatterns(result.patterns);
        // デバッグ情報をコンソールに表示
        if (result.debug_info) {
          console.log('=== パターン生成デバッグ情報 ===');
          console.log('Gemini プロンプト:', result.debug_info.prompt);
          console.log('アンケートデータ:', result.debug_info.survey_data);
          console.log('計算された評価:', result.debug_info.ratings);
          console.log('生成されたパターン:', result.debug_info.generated_patterns);
          console.log('=============================');
        }
      } else {
        alert('パターン生成に失敗しました: ' + result.error);
      }
    } catch (err) {
      alert('パターン生成中にエラーが発生しました');
    } finally {
      setGeneratingPatterns(false);
    }
  };

  const selectPattern = (pattern: DraftPattern) => {
    setEditedContent(pattern.content);
    if (draftData) {
      setDraftData({
        ...draftData,
        draft_content: pattern.content
      });
    }
  };

  const copyAndOpenGoogleMaps = async () => {
    try {
      // クリップボードにコピー
      await navigator.clipboard.writeText(editedContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // 2秒後にリセット
      
      // Google Mapsを開く
      const googleMapsUrl = `https://www.google.com/maps/search/${encodeURIComponent(draftData?.shop_name || '')}`;
      window.open(googleMapsUrl, '_blank');
    } catch (err) {
      // フォールバック（古いブラウザ対応）
      const textArea = document.createElement('textarea');
      textArea.value = editedContent;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      
      // Google Mapsを開く
      const googleMapsUrl = `https://www.google.com/maps/search/${encodeURIComponent(draftData?.shop_name || '')}`;
      window.open(googleMapsUrl, '_blank');
    }
  };

  const formatExpiresAt = (timestamp: number): string => {
    const expiresDate = new Date(timestamp * 1000);
    const now = new Date();
    const diff = expiresDate.getTime() - now.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    
    if (minutes <= 0) {
      return '期限切れ';
    } else if (minutes < 60) {
      return `あと${minutes}分`;
    } else {
      const hours = Math.floor(minutes / 60);
      return `あと${hours}時間${minutes % 60}分`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">エラー</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            ホームに戻る
          </button>
        </div>
      </div>
    );
  }

  if (!draftData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* ヘッダー */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                口コミ下書き編集
              </h1>
              <p className="text-gray-600 mt-1">
                {draftData.shop_name}
              </p>
            </div>
            <div className="text-right">
              <div className="flex items-center text-sm text-gray-500 mb-2">
                <Clock className="h-4 w-4 mr-1" />
                {formatExpiresAt(draftData.expires_at)}
              </div>
              {draftData.user_email && (
                <div className="flex items-center text-sm text-gray-500">
                  <Mail className="h-4 w-4 mr-1" />
                  バックアップ送信済み
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          {/* 下書き編集エリア */}
          <div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center mb-4">
                <Edit2 className="h-5 w-5 text-blue-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-800">
                  下書き内容
                </h2>
              </div>

              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                rows={8}
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="口コミ内容を編集してください..."
              />

              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-gray-500">
                  {editedContent.length} 文字
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={regenerateDraft}
                    disabled={regenerating}
                    className="flex items-center px-4 py-2 text-blue-600 hover:text-blue-700 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`h-4 w-4 mr-1 ${regenerating ? 'animate-spin' : ''}`} />
                    {regenerating ? '再生成中...' : '再生成'}
                  </button>
                  <button
                    onClick={copyAndOpenGoogleMaps}
                    disabled={!editedContent}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4 mr-1" />
                        コピー済み
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-1" />
                        コピーして開く
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* パターン選択エリア */}
            {showPatterns && patterns.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6 mt-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  下書きパターン選択
                </h2>
                <p className="text-sm text-gray-600 mb-6">
                  お好みのパターンを選択してください。選択すると上記の編集エリアに反映されます。
                </p>

                <div className="space-y-6">
                  {patterns.map((pattern) => (
                    <div key={pattern.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium text-gray-800">
                          {pattern.title}
                          {pattern.id === 1 && <span className="text-xs text-blue-600 ml-2">(詳細重視)</span>}
                          {pattern.id === 2 && <span className="text-xs text-green-600 ml-2">(簡潔型)</span>}
                          {pattern.id === 3 && <span className="text-xs text-purple-600 ml-2">(バランス型)</span>}
                        </h3>
                        <button
                          onClick={() => selectPattern(pattern)}
                          className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 transition-colors"
                        >
                          このパターンを選択
                        </button>
                      </div>
                      <div className="bg-gray-50 rounded-md p-3">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {pattern.content}
                        </p>
                        <div className="text-xs text-gray-500 mt-2">
                          文字数: {pattern.content.length}文字
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ローディング表示 */}
            {generatingPatterns && (
              <div className="bg-white rounded-lg shadow-md p-6 mt-6">
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600 mr-3"></div>
                  <span className="text-gray-600">パターンを生成中...</span>
                </div>
              </div>
            )}

            {/* 投稿ガイド */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
              <h3 className="text-lg font-semibold text-blue-800 mb-3">
                📱 Google Mapsへの投稿方法
              </h3>
              <div className="space-y-2 text-blue-700 text-sm">
                <p>1. 上記の下書きをコピーしてください</p>
                <p>2. Google Mapsで「{draftData.shop_name}」を検索</p>
                <p>3. 「口コミを書く」ボタンをタップ</p>
                <p>4. 下書きを貼り付けて、星評価を選択</p>
                <p>5. 「投稿」ボタンで完了！</p>
              </div>
              <button
                onClick={() => setShowInstructions(true)}
                className="mt-4 flex items-center text-blue-600 hover:text-blue-700 font-medium"
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                詳しい手順を見る
              </button>
            </div>

            {/* 重要な注意点 */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
              <h3 className="text-sm font-semibold text-yellow-800 mb-2">
                ⚠️ 重要な注意点
              </h3>
              <div className="space-y-1 text-xs text-yellow-700">
                <p>• 投稿は完全にお客様の判断です</p>
                <p>• 下書きは{formatExpiresAt(draftData.expires_at)}で削除されます</p>
                <p>• 個人情報は不要です</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 投稿手順モーダル */}
      {showInstructions && (
        <PublishInstructions
          shopName={draftData.shop_name}
          onClose={() => setShowInstructions(false)}
        />
      )}

    </div>
  );
};

export default DraftEditor;